import type { DataSource, Entity, NewsArticle, NewsEntity } from '../types/entity';
import { apiFetch } from '../lib/apiFetch';

/**
 * APITube News — Free tier tem ATRASO DE 12H nos dados (não é tempo
 * real). Por isso: severity sempre 'normal', e esta é a única camada
 * do painel que não usa o ping de assinatura visual (ver main.ts, onde
 * o EntityRenderer desta camada é instanciado sem o elemento de ping —
 * ou, mais precisamente, onde a decisão de "sem pin no mapa" é tratada
 * no LayerOrchestrator).
 *
 * Fallback do lado do client: se o fetch falhar, mantém o último
 * snapshot bem-sucedido em memória (`lastGoodSnapshot`) em vez de
 * limpar a lista — mesma filosofia de degradação graciosa que o
 * scheduled job já aplica no backend (ver functions/src/proxies/news.ts).
 */

interface NewsProxyArticle {
  id: number;
  href: string;
  publishedAt: string;
  title: string;
  description: string | null;
  sourceDomain: string | null;
  location: { lat: number; lng: number } | null;
}

interface NewsProxyResponse {
  articles: NewsProxyArticle[];
}

/** Último snapshot bem-sucedido — sobrevive a falhas de fetch
 *  subsequentes. Módulo-level porque só há uma instância desta fonte
 *  no app (não é uma factory como Windy/WSDOT). */
let lastGoodSnapshot: NewsArticle[] = [];

function toNewsArticle(article: NewsProxyArticle): NewsArticle {
  const base = {
    id: `news-${article.id}`,
    layer: 'news' as const,
    label: article.title,
    severity: 'normal' as const,
    observedAt: article.publishedAt,
    raw: {
      href: article.href,
      description: article.description,
      sourceDomain: article.sourceDomain,
    },
  };

  if (article.location) {
    return {
      ...base,
      lat: article.location.lat,
      lon: article.location.lng,
      hasLocation: true,
    };
  }

  return {
    ...base,
    hasLocation: false,
  };
}

/** Filtra só os artigos com coordenada — são os únicos que viram
 *  marker no mapa. Usado tanto por `fetchEntities` quanto exposto
 *  separadamente caso o HUD precise só da parte "mapeável". */
function onlyWithLocation(articles: NewsArticle[]): NewsEntity[] {
  return articles.filter((a): a is NewsEntity & { hasLocation: true } => a.hasLocation);
}

/** Snapshot completo (com e sem coordenada) — é o que o componente de
 *  lista/ticker do HUD deve consumir, não `fetchEntities()` (que só
 *  devolve o subconjunto com posição no mapa). */
export function getLatestNewsSnapshot(): NewsArticle[] {
  return lastGoodSnapshot;
}

export const newsSource: DataSource = {
  layer: 'news',
  label: 'Notícias (APITube)',
  requiresClientKey: false,
  viaCloudFunction: true,

  async fetchEntities(): Promise<Entity[]> {
    try {
      const res = await apiFetch('/news');
      if (!res.ok) {
        throw new Error(`Proxy news respondeu ${res.status}`);
      }

      const data = (await res.json()) as NewsProxyResponse;
      const articles = data.articles.map(toNewsArticle);

      lastGoodSnapshot = articles;
      return onlyWithLocation(articles);
    } catch (err) {
      // Fallback: mantém o snapshot anterior, não limpa a lista.
      console.warn('[newsSource] falha ao buscar /api/news, mantendo snapshot anterior', err);
      return onlyWithLocation(lastGoodSnapshot);
    }
  },
};
